import React, {useState, useEffect, useRef, useMemo} from 'react'
import {KTSVG, QUERIES, useDebounce} from '../../../../_metronic/helpers'
import {TagsAutoComplete} from '../../../../_metronic/partials/modals/task/components/TagsAutoComplete'
import {Tag} from '../../../../_metronic/partials/modals/task/core/_models'
import {Accordion, Card, OverlayTrigger, Tooltip} from 'react-bootstrap'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import FilterAlt from '@mui/icons-material/FilterAlt'
import {useQuery} from 'react-query'
import {getAllClients} from '../../clients/components/core/_requests'
import {Project} from '../../project/components/core/_models'
import Select, {MultiValue} from 'react-select'
import {useSearchParams} from 'react-router-dom'
import {getUsersInstanceTask} from '../../../../_metronic/partials/modals/task/core/_requests'
import {DateRangeChip} from './DateRangeChip'
import {DateRange, emptyDateRange, TaskFilterBarProps} from './types'
import './TaskFilterBar.css'

type ClientOption = {value: string; label: string}
type ProjectOption = {value: string; label: string}
type PriorityOption = {value: string; label: string}

const reactSelectStyles = {
  control: (base: any) => ({...base, cursor: 'pointer'}),
  valueContainer: (base: any) => ({
    ...base,
    maxHeight: '48px',
    overflowY: 'auto',
  }),
  multiValue: (provided: any) => ({
    ...provided,
    borderRadius: '10px',
    backgroundColor: 'var(--tr-green)',
    padding: '0px 4px',
  }),
  multiValueLabel: (provided: any) => ({...provided, color: 'var(--tr-ink)', fontSize: '12px'}),
  multiValueRemove: (provided: any) => ({
    ...provided,
    padding: '0px',
    margin: '0px',
    color: 'var(--tr-ink)',
    ':hover': {backgroundColor: 'none'},
  }),
  // Mesmo padrão de cor já usado em TagsAutoComplete.tsx pra opção selecionada/focada
  // (verde da marca com alpha), em vez do bg-primary/azul padrão do react-select.
  option: (styles: any, {isDisabled, isFocused, isSelected}: any) => ({
    ...styles,
    backgroundColor: isDisabled ? undefined : isSelected || isFocused ? '#b5d44e70' : undefined,
    color: isDisabled ? '#ccc' : isSelected ? '#181c32' : undefined,
  }),
}

const reactSelectClassNames = {
  control: (state: any) =>
    state.isFocused
      ? 'form-control form-control-solid border-primary p-0'
      : 'form-control form-control-solid p-0',
  placeholder: () => 'text-muted',
  input: () => 'text-dark',
  singleValue: () => 'text-dark',
  menu: () => 'bg-body',
}

const priorityOptions: PriorityOption[] = [
  {value: 'baixa', label: 'Baixa'},
  {value: 'media', label: 'Média'},
  {value: 'alta', label: 'Alta'},
  {value: 'critical', label: 'Crítica'},
]

const TaskFilterBar = ({
  scope,
  hideDatesColumn,
  onHideDatesColumnChange,
  onApply,
  onSearchChange,
  initialFilter,
  exportButton,
  responsibleUsers,
}: TaskFilterBarProps) => {
  const isInstance = scope === 'instance'
  const isProjectScope = scope === 'project'
  // Em scope='project' o filtro de "Responsável" vem de fora (membros do projeto);
  // em scope='instance' o próprio componente busca via getUsersInstanceTask (abaixo).
  const externalResponsibleUsers = !isInstance ? responsibleUsers : undefined
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebounce(searchTerm, 1000)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const hasHydratedOverdueFromUrl = useRef(false)
  const hasHydratedPriorityFromUrl = useRef(false)
  const hasHydratedDeliveryFromUrl = useRef(false)
  const [selectedStatus, setSelectedStatus] = useState('Aberta')
  const [selectedOptions, setSelectedOptions] = useState<Tag[]>([])
  const [searchParams, setSearchParams] = useSearchParams()
  const [priorities, setPriorities] = useState<string[]>(() => {
    const rawPriority = searchParams.get('priority')?.trim().toLowerCase()
    if (!rawPriority) return []
    if (rawPriority === 'critical' || rawPriority === 'critica') return ['critical']
    if (rawPriority === 'high' || rawPriority === 'alta') return ['alta']
    if (rawPriority === 'medium' || rawPriority === 'media') return ['media']
    if (rawPriority === 'low' || rawPriority === 'baixa') return ['baixa']
    return []
  })
  const [availableProjects, setAvailableProjects] = useState<Project[]>([])
  const [selectedClients, setSelectedClients] = useState<ClientOption[]>([])
  const [selectedProjects, setSelectedProjects] = useState<ProjectOption[]>([])
  const [onlyOverdueTasks, setOnlyOverdueTasks] = useState(false)
  const [filterNearDueDate, setFilterNearDueDate] = useState(false)
  const [onlyWithSubtasks, setOnlyWithSubtasks] = useState(false)
  const [sortByDeliveryDate, setSortByDeliveryDate] = useState(true)
  const [beginDate, setBeginDate] = useState<DateRange>(emptyDateRange)
  const [expectedDate, setExpectedDate] = useState<DateRange>(emptyDateRange)
  const [createdAt, setCreatedAt] = useState<DateRange>(emptyDateRange)
  const [endDate, setEndDate] = useState<DateRange>(emptyDateRange)
  const [deliveryPunctuality, setDeliveryPunctuality] = useState<'' | 'on_time' | 'late'>('')
  const today = new Date()

  const {data: clients} = useQuery(
    `${QUERIES.CLIENTS_LIST}-all`,
    async () => (await getAllClients()).data,
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  )

  const {data: instanceUsers} = useQuery(
    ['instance-tasks-users'],
    async () => {
      const response = await getUsersInstanceTask()
      return (response as any)?.users ?? []
    },
    {enabled: isInstance, cacheTime: 0, refetchOnWindowFocus: false}
  )

  // Fonte de usuários pro campo "Responsável": instância inteira (scope='instance') ou lista
  // fornecida pela rota (scope='project', membros do projeto). Nos demais scopes, o campo some.
  const effectiveResponsibleUsers = isInstance ? instanceUsers : externalResponsibleUsers
  const showResponsibleField = isInstance || externalResponsibleUsers !== undefined

  const [selectedUsers, setSelectedUsers] = useState<Array<{user_id: number; name: string}>>([])

  const selectedUserIdsForApi = useMemo(
    () =>
      selectedUsers.length
        ? selectedUsers.map((u) => Number(u.user_id)).filter(Boolean)
        : undefined,
    [selectedUsers]
  )

  useEffect(() => {
    if (hasHydratedOverdueFromUrl.current) return
    const overdueParam = searchParams.get('overdue')
    const shouldEnableOverdue = overdueParam === 'true' || overdueParam === '1'
    if (shouldEnableOverdue) {
      setOnlyOverdueTasks(true)
      onApply({only_overdue_tasks: true})
    }
    hasHydratedOverdueFromUrl.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!showResponsibleField) {
      setSelectedUsers([])
      return
    }
    const userIds = initialFilter?.user_ids
    if (
      Array.isArray(userIds) &&
      userIds.length > 0 &&
      Array.isArray(effectiveResponsibleUsers) &&
      effectiveResponsibleUsers.length > 0
    ) {
      const matched = userIds
        .map((id: number) => {
          const u = (effectiveResponsibleUsers as any[]).find(
            (p) => Number(p.id ?? p.user_id) === Number(id)
          )
          return u ? {user_id: Number(u.id ?? u.user_id), name: String(u.name)} : null
        })
        .filter(Boolean) as Array<{user_id: number; name: string}>
      setSelectedUsers(matched)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResponsibleField, effectiveResponsibleUsers])

  useEffect(() => {
    if (hasHydratedPriorityFromUrl.current) return
    const rawPriority = searchParams.get('priority')?.trim().toLowerCase()
    if (!rawPriority) {
      hasHydratedPriorityFromUrl.current = true
      return
    }
    const priorityMap: Record<string, string> = {
      critical: 'critical',
      critica: 'critical',
      high: 'alta',
      alta: 'alta',
      medium: 'media',
      media: 'media',
      low: 'baixa',
      baixa: 'baixa',
    }
    const mappedPriority = priorityMap[rawPriority]
    if (mappedPriority) {
      setPriorities([mappedPriority])
      onApply({priority: [mappedPriority]})
    }
    hasHydratedPriorityFromUrl.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Deep-link dos cards "Entregues no prazo / com atraso" do dashboard do colaborador:
  // /mytasks?status=fechada&punctuality=on_time&endFrom=...&endTo=...
  useEffect(() => {
    if (hasHydratedDeliveryFromUrl.current) return
    const punctuality = searchParams.get('punctuality')
    const status = searchParams.get('status')
    const endFrom = searchParams.get('endFrom') ?? ''
    const endTo = searchParams.get('endTo') ?? ''
    const validPunctuality = punctuality === 'on_time' || punctuality === 'late' ? punctuality : ''

    if (validPunctuality || status === 'fechada') {
      setSelectedStatus('Fechada')
      if (validPunctuality) setDeliveryPunctuality(validPunctuality)
      if (endFrom || endTo) setEndDate({begin_date: endFrom, end_date: endTo})
      onApply({
        status: 'fechada',
        ...(validPunctuality ? {delivery_punctuality: validPunctuality} : {}),
        ...(endFrom || endTo ? {end_date: {begin_date: endFrom, end_date: endTo}} : {}),
      })
    }
    hasHydratedDeliveryFromUrl.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const searchLength = debouncedSearchTerm?.length ?? 0
    if (searchLength === 0 || searchLength >= 3) {
      onSearchChange(debouncedSearchTerm ?? '')
      setTimeout(() => searchInputRef.current?.focus(), 100)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm])

  const [activeKey, setActiveKey] = useState<string | null>(null)

  const getClientOptions = (): ClientOption[] =>
    clients?.map((client) => ({value: client.id || '', label: client.name || ''})) || []

  const getProjectOptions = (): ProjectOption[] =>
    availableProjects.map((project) => ({value: project.id || '', label: project.name || ''}))

  const selectedClientIds = selectedClients
    .map((c) => Number(c.value))
    .filter((id) => !Number.isNaN(id))
  const selectedProjectIds = selectedProjects
    .map((p) => Number(p.value))
    .filter((id) => !Number.isNaN(id))

  const handleClientsChange = (newValue: MultiValue<ClientOption>) => {
    const nextSelectedClients = Array.isArray(newValue) ? newValue : []
    setSelectedClients(nextSelectedClients)
    const nextClientIds = nextSelectedClients
      .map((c) => Number(c.value))
      .filter((id) => !Number.isNaN(id))
    if (nextClientIds.length === 0) {
      setAvailableProjects([])
      setSelectedProjects([])
      return
    }
    const nextAvailableProjects = (clients ?? [])
      .filter((client) => nextClientIds.includes(Number(client.id)))
      .flatMap((client) => client.projects ?? [])
    const deduped = Array.from(new Map(nextAvailableProjects.map((p) => [p.id, p])).values())
    setAvailableProjects(deduped)
    const nextProjectIdSet = new Set(deduped.map((p) => Number(p.id)))
    setSelectedProjects((prev) => prev.filter((p) => nextProjectIdSet.has(Number(p.value))))
  }

  const handleProjectsChange = (newValue: MultiValue<ProjectOption>) => {
    setSelectedProjects(Array.isArray(newValue) ? newValue : [])
  }

  const selectedPriorityOptions = priorityOptions.filter((o) => priorities.includes(o.value))

  const handlePrioritiesChange = (newValue: MultiValue<PriorityOption>) => {
    setPriorities(Array.isArray(newValue) ? newValue.map((o) => o.value) : [])
  }

  function handleFilterClick() {
    const isDelivered = selectedStatus.toLowerCase() === 'fechada'
    onApply({
      priority: priorities.length ? priorities : undefined,
      status: selectedStatus.toLowerCase(),
      tags: selectedOptions.length ? selectedOptions.flatMap((tag) => Number(tag.id)) : undefined,
      client_id: selectedClientIds.length ? selectedClientIds : undefined,
      project_id: selectedProjectIds.length ? selectedProjectIds : undefined,
      begin_date: beginDate,
      expected_date: expectedDate,
      created_at: createdAt,
      end_date: endDate.begin_date || endDate.end_date ? endDate : undefined,
      delivery_punctuality: isDelivered && deliveryPunctuality ? deliveryPunctuality : undefined,
      only_overdue_tasks: onlyOverdueTasks,
      near_due_date: filterNearDueDate,
      only_with_subtasks: onlyWithSubtasks,
      sort_by_delivery_date: sortByDeliveryDate,
      user_ids: showResponsibleField ? selectedUserIdsForApi : undefined,
    })
  }

  function handleClearFilterClick() {
    setPriorities([])
    setSelectedStatus('Aberta')
    setSelectedOptions([])
    setSelectedClients([])
    setSelectedProjects([])
    setAvailableProjects([])
    setSelectedUsers([])
    setOnlyOverdueTasks(false)
    setFilterNearDueDate(false)
    setOnlyWithSubtasks(false)
    setSortByDeliveryDate(true)
    setBeginDate(emptyDateRange)
    setExpectedDate(emptyDateRange)
    setCreatedAt(emptyDateRange)
    setEndDate(emptyDateRange)
    setDeliveryPunctuality('')
    setSearchTerm('')
    onSearchChange('')
    // "Limpar" só resetava o formulário local -- o filtro de verdade (state.filter)
    // continuava com os valores antigos até um novo "Filtrar", então a lista/kanban
    // não voltava a mostrar tudo. Aplica o reset de verdade também.
    onApply({
      priority: undefined,
      status: 'aberta',
      tags: undefined,
      client_id: undefined,
      project_id: undefined,
      begin_date: emptyDateRange,
      expected_date: emptyDateRange,
      created_at: emptyDateRange,
      end_date: undefined,
      delivery_punctuality: undefined,
      only_overdue_tasks: false,
      near_due_date: false,
      only_with_subtasks: false,
      sort_by_delivery_date: true,
      user_ids: undefined,
    })
  }

  function handleChangeOnlyOverdueTasks(e: React.ChangeEvent<HTMLInputElement>) {
    setOnlyOverdueTasks(e.target.checked)
    onApply({only_overdue_tasks: e.target.checked})
  }

  function handleChangeFilterNearDueDate(e: React.ChangeEvent<HTMLInputElement>) {
    setFilterNearDueDate(e.target.checked)
    onApply({near_due_date: e.target.checked})
  }

  function handleChangeSortByDeliveryDate(e: React.ChangeEvent<HTMLInputElement>) {
    setSortByDeliveryDate(e.target.checked)
    onApply({sort_by_delivery_date: e.target.checked})
  }

  function handleChangeOnlyWithSubtasks(e: React.ChangeEvent<HTMLInputElement>) {
    setOnlyWithSubtasks(e.target.checked)
  }

  return (
    <div className='card-header bg-body align-items-center border-bottom py-6 mb-4 justify-content-end w-100'>
      <Accordion activeKey={activeKey} className='d-flex w-100'>
      <Card className='d-flex w-100'>
        <Card.Header className='d-flex flex-column flex-md-row align-items-stretch align-items-md-center border-0 px-0 gap-3 gap-md-4 justify-content-between'>
          <ul className='nav nav-pills mb-sm-0 d-flex flex-column flex-md-row align-items-stretch align-items-md-center flex-md-wrap gap-2 order-1 order-md-2 ms-md-auto'>
            <li className='d-flex align-items-center position-relative flex-nowrap'>
              <OverlayTrigger
                placement='bottom'
                delay={{show: 300, hide: 100}}
                overlay={
                  <Tooltip id='tooltip-overdue' style={{maxWidth: '300px'}}>
                    <div className='text-start'>
                      <strong>Exibir apenas tarefas em atraso</strong>
                      <br />
                      Mostra somente tarefas cujo prazo de entrega já expirou e ainda não foram
                      concluídas.
                    </div>
                  </Tooltip>
                }
              >
                <label className='form-check form-switch form-check-custom form-check-solid d-flex align-items-center'>
                  <input
                    className='form-check-input h-20px w-30px me-2'
                    type='checkbox'
                    role='button'
                    onChange={handleChangeOnlyOverdueTasks}
                    checked={onlyOverdueTasks}
                  />
                  <i
                    className={`bi bi-exclamation-triangle-fill me-2 ${
                      onlyOverdueTasks ? 'text-danger' : 'text-muted'
                    }`}
                  ></i>
                  <span
                    className={`${
                      onlyOverdueTasks ? 'fw-bold text-gray-900' : 'text-gray-600'
                    } fs-7`}
                  >
                    Exibir apenas tarefas em atraso
                  </span>
                </label>
              </OverlayTrigger>
            </li>

            <li className='d-flex align-items-center position-relative flex-nowrap'>
              <OverlayTrigger
                placement='bottom'
                delay={{show: 300, hide: 100}}
                overlay={
                  <Tooltip id='tooltip-near-due' style={{maxWidth: '300px'}}>
                    <div className='text-start'>
                      <strong>Próximas do vencimento</strong>
                      <br />
                      Exibe tarefas que vencem nos próximos 7 dias.
                    </div>
                  </Tooltip>
                }
              >
                <label className='form-check form-switch form-check-custom form-check-solid d-flex align-items-center'>
                  <input
                    className='form-check-input h-20px w-30px me-2'
                    type='checkbox'
                    role='button'
                    onChange={handleChangeFilterNearDueDate}
                    checked={filterNearDueDate}
                  />
                  <i
                    className={`bi bi-clock-fill me-2 ${
                      filterNearDueDate ? 'text-warning' : 'text-muted'
                    }`}
                  ></i>
                  <span
                    className={`${
                      filterNearDueDate ? 'fw-bold text-gray-900' : 'text-gray-600'
                    } fs-7`}
                  >
                    Próximas do vencimento
                  </span>
                </label>
              </OverlayTrigger>
            </li>

            <li
              className='mx-1 text-muted d-none d-md-block'
              style={{fontSize: '20px', userSelect: 'none'}}
            >
              |
            </li>

            {/* Some abaixo de md: a coluna Datas já é ocultada por padrão no layout mobile
                (ver mobileOrder/useIsMobile nas tabelas), então o toggle fica redundante/
                confuso lá -- não existe "coluna Datas visível" nesse tamanho de tela pra
                ocultar. */}
            <li className='d-none d-md-flex align-items-center position-relative flex-nowrap'>
              <OverlayTrigger
                placement='bottom'
                delay={{show: 300, hide: 100}}
                overlay={
                  <Tooltip id='tooltip-hide-dates' style={{maxWidth: '300px'}}>
                    <div className='text-start'>
                      <strong>Ocultar coluna Datas</strong>
                      <br />
                      Oculta a coluna de datas (início e fim) da tabela para economizar espaço.
                    </div>
                  </Tooltip>
                }
              >
                <label className='form-check form-switch form-check-custom form-check-solid d-flex align-items-center'>
                  <input
                    className='form-check-input h-20px w-30px me-2'
                    type='checkbox'
                    role='button'
                    onChange={(e) => onHideDatesColumnChange(e.target.checked)}
                    checked={hideDatesColumn}
                  />
                  <i
                    className={`bi bi-eye-slash-fill me-2 ${
                      hideDatesColumn ? 'tfb-active-accent' : 'text-muted'
                    }`}
                  ></i>
                  <span
                    className={`${
                      hideDatesColumn ? 'fw-bold text-gray-900' : 'text-gray-600'
                    } fs-7`}
                  >
                    Ocultar coluna Datas
                  </span>
                </label>
              </OverlayTrigger>
            </li>

            <li
              className='mx-1 text-muted d-none d-md-block'
              style={{fontSize: '20px', userSelect: 'none'}}
            >
              |
            </li>

            <li className='d-flex align-items-center position-relative flex-nowrap'>
              <OverlayTrigger
                placement='bottom'
                delay={{show: 300, hide: 100}}
                overlay={
                  <Tooltip id='tooltip-sort-delivery' style={{maxWidth: '300px'}}>
                    <div className='text-start'>
                      <strong>Ordenar por data de entrega</strong>
                      <br />
                      Ordena as tarefas por prazo de entrega (próximas primeiro). Padrão do sistema.
                    </div>
                  </Tooltip>
                }
              >
                <label className='form-check form-switch form-check-custom form-check-solid d-flex align-items-center'>
                  <input
                    className='form-check-input h-20px w-30px me-2'
                    type='checkbox'
                    role='button'
                    onChange={handleChangeSortByDeliveryDate}
                    checked={sortByDeliveryDate}
                  />
                  <i
                    className={`bi bi-calendar-event-fill me-2 ${
                      sortByDeliveryDate ? 'tfb-active-accent' : 'text-muted'
                    }`}
                  ></i>
                  <span
                    className={`${
                      sortByDeliveryDate ? 'fw-bold text-gray-900' : 'text-gray-600'
                    } fs-7`}
                  >
                    Ordenar por data de entrega
                  </span>
                </label>
              </OverlayTrigger>
            </li>

            <li className='d-flex flex-nowrap gap-2 ms-auto'>
              <div
                className='nav-item m-0'
                onClick={() => {
                  // Trocar de visualização não deve descartar o filtro aplicado -- só fecha
                  // o painel e muda o modo (mode em si não afeta o resultado, só qual
                  // componente renderiza; o filtro continua o mesmo nos dois).
                  setActiveKey(null)
                  onApply({mode: 'table'})
                  const newParams = new URLSearchParams(searchParams)
                  newParams.set('mode', 'table')
                  setSearchParams(newParams)
                }}
              >
                <a
                  className={`btn btn-sm btn-icon btn-light btn-color-muted tfb-view-toggle ${
                    searchParams.get('mode') != 'kanban' && 'active'
                  }`}
                >
                  <span className='svg-icon svg-icon-2'>
                    <svg
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M21 7H3C2.4 7 2 6.6 2 6V4C2 3.4 2.4 3 3 3H21C21.6 3 22 3.4 22 4V6C22 6.6 21.6 7 21 7Z'
                        fill='currentColor'
                      />
                      <path
                        opacity='0.3'
                        d='M21 14H3C2.4 14 2 13.6 2 13V11C2 10.4 2.4 10 3 10H21C21.6 10 22 10.4 22 11V13C22 13.6 21.6 14 21 14ZM22 20V18C22 17.4 21.6 17 21 17H3C2.4 17 2 17.4 2 18V20C2 20.6 2.4 21 3 21H21C21.6 21 22 20.6 22 20Z'
                        fill='currentColor'
                      />
                    </svg>
                  </span>
                </a>
              </div>
              <div
                onClick={() => {
                  setActiveKey(null)
                  onApply({mode: 'kanban'})
                  const newParams = new URLSearchParams(searchParams)
                  newParams.set('mode', 'kanban')
                  setSearchParams(newParams)
                }}
              >
                <a
                  className={`btn btn-sm btn-icon btn-light btn-color-muted tfb-view-toggle ${
                    searchParams.get('mode') == 'kanban' && 'active'
                  }`}
                >
                  <ViewColumnIcon className='w-5 h-5' />
                </a>
              </div>
            </li>
          </ul>

          <div className='d-flex align-items-center gap-3 flex-nowrap order-2 order-md-1 w-100 w-md-auto'>
            <div
              className='d-flex flex-column position-relative my-1 w-100'
              style={{minWidth: '250px'}}
            >
              <div className='d-flex align-items-center position-relative'>
                <KTSVG
                  path='/media/icons/duotune/general/gen021.svg'
                  className='svg-icon-1 position-absolute ms-3'
                />
                <input
                  ref={searchInputRef}
                  type='text'
                  data-kt-client-table-filter='search'
                  className='form-control form-control-solid ps-15'
                  placeholder='Buscar tarefa'
                  aria-label='Buscar tarefa'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              {searchTerm.length > 0 && searchTerm.length < 3 && (
                <small className='text-muted mt-1 ms-1'>
                  Digite ao menos 3 caracteres para buscar
                </small>
              )}
            </div>
            <OverlayTrigger
              placement='top'
              delay={{show: 50, hide: 100}}
              overlay={<Tooltip>Filtros avançados</Tooltip>}
            >
              <button
                className='btn btn-sm btn-icon btn-light btn-hover-primary'
                onClick={() => setActiveKey((prev) => (prev == '0' ? null : '0'))}
              >
                <FilterAlt className='h-5 w-5' />
              </button>
            </OverlayTrigger>
            {exportButton}
          </div>
        </Card.Header>

        <Accordion.Collapse eventKey='0'>
          <Card.Body className='d-flex flex-column gap-6 flex-wrap px-6 py-6'>
            {/* Seção: Quem / o quê */}
            <div className='d-flex flex-column gap-3 w-100 tfb-section-divider'>
              <span className='fs-7 text-uppercase fw-bold text-muted'>Quem / o quê</span>
              <div className='d-flex gap-5 flex-wrap w-100'>
                {/* Em scope='project' a tela já está dentro de um único projeto — os campos
                    Clientes/Projetos não fazem sentido aqui (a TasksListSearchComponent original
                    desta rota nunca os teve). */}
                {!isProjectScope && (
                  <>
                    <div className='d-flex align-items-end position-relative flex-wrap w-100 w-md-auto'>
                      <div className='d-flex flex-column flex-wrap w-100'>
                        <label className='d-block fs-7 mb-1'>Clientes</label>
                        <Select
                          value={selectedClients}
                          onChange={handleClientsChange}
                          options={getClientOptions()}
                          placeholder='Selecione'
                          isClearable
                          isSearchable
                          isMulti
                          closeMenuOnSelect={false}
                          className='w-100 w-md-200px react-select-container'
                          classNamePrefix='react-select'
                          styles={reactSelectStyles}
                          classNames={reactSelectClassNames}
                        />
                      </div>
                    </div>

                    <div className='d-flex align-items-end position-relative flex-wrap w-100 w-md-auto'>
                      <div className='d-flex flex-column w-100'>
                        <label className='d-block fs-7 mb-1'>Projetos</label>
                        <Select
                          value={selectedProjects}
                          onChange={handleProjectsChange}
                          options={getProjectOptions()}
                          placeholder='Selecione'
                          isClearable
                          isSearchable
                          isMulti
                          closeMenuOnSelect={false}
                          isDisabled={selectedClients.length === 0}
                          className='w-100 w-md-200px react-select-container'
                          classNamePrefix='react-select'
                          styles={reactSelectStyles}
                          classNames={reactSelectClassNames}
                        />
                      </div>
                    </div>
                  </>
                )}

                {searchParams.get('mode') != 'kanban' && (
                  <div className='d-flex align-items-end position-relative flex-wrap w-100 w-md-auto'>
                    <div className='d-flex flex-column flex-wrap w-100'>
                      <label className='d-block fs-7 mb-1'>Status</label>
                      <select
                        className='form-select form-select-sm form-control form-control-solid place-holder w-100 w-md-200px tfb-native-select-md'
                        onChange={(e) => {
                          setSelectedStatus(e.target.value)
                          if (e.target.value.toLowerCase() !== 'fechada') setDeliveryPunctuality('')
                        }}
                        value={selectedStatus || 'Aberta'}
                      >
                        <option value='Aberta'>Tarefas abertas</option>
                        <option value='Fechada'>Tarefas entregues</option>
                        {scope === 'mine' && (
                          <option value='CriadasPorMim'>Tarefas criadas por mim</option>
                        )}
                      </select>
                    </div>
                  </div>
                )}

                <div className='d-flex align-items-end position-relative flex-wrap w-100 w-md-auto'>
                  <div className='d-flex flex-column w-100'>
                    <label className='d-block fs-7 mb-1'>Prioridade</label>
                    <Select
                      value={selectedPriorityOptions}
                      onChange={handlePrioritiesChange}
                      options={priorityOptions}
                      placeholder='Selecione'
                      isClearable
                      isSearchable={false}
                      isMulti
                      closeMenuOnSelect={false}
                      className='w-100 w-md-200px react-select-container'
                      classNamePrefix='react-select'
                      styles={reactSelectStyles}
                      classNames={reactSelectClassNames}
                    />
                  </div>
                </div>

                <div className='d-flex align-items-end flex-wrap w-100 w-md-auto'>
                  <div className='d-flex flex-column w-100 w-md-auto'>
                    <label className='d-block fs-7 mb-1'>Tags</label>
                    <TagsAutoComplete
                      actions={false}
                      isSearchable={false}
                      selectedOptions={selectedOptions}
                      setSelectedOptions={setSelectedOptions}
                      controlClass='ps-2 py-1 fs-6'
                      className='w-100 w-md-200px'
                    />
                  </div>
                </div>

                {showResponsibleField && (
                  <div className='d-flex align-items-end position-relative flex-wrap w-100 w-md-auto'>
                    <div className='d-flex flex-column w-100'>
                      <label className='d-block fs-7 mb-1'>Responsável</label>
                      <Select
                        isMulti
                        closeMenuOnSelect={false}
                        value={selectedUsers.map((u) => ({
                          value: Number(u.user_id),
                          label: u.name,
                        }))}
                        onChange={(newValue: MultiValue<{value: number; label: string}>) => {
                          setSelectedUsers(
                            (newValue ?? []).map((o) => ({user_id: o.value, name: o.label}))
                          )
                        }}
                        options={(effectiveResponsibleUsers ?? []).map((u: any) => ({
                          value: Number(u.id ?? u.user_id),
                          label: String(u.name),
                        }))}
                        placeholder='Selecione os responsáveis'
                        isClearable
                        isSearchable
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => String(option.value)}
                        noOptionsMessage={() => 'Nenhum responsável disponível'}
                        className='w-100 w-md-300px react-select-container'
                        classNamePrefix='react-select'
                        styles={reactSelectStyles}
                        classNames={reactSelectClassNames}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Seção: Quando */}
            <div className='d-flex flex-column gap-3 w-100 tfb-section-divider'>
              <span className='fs-7 text-uppercase fw-bold text-muted'>Quando</span>
              <div className='d-flex gap-5 flex-wrap w-100'>
                <DateRangeChip
                  label='Criação'
                  value={createdAt}
                  onChange={setCreatedAt}
                  maxDate={today}
                />
                <DateRangeChip label='Início' value={beginDate} onChange={setBeginDate} />
                <DateRangeChip
                  label='Previsão de entrega'
                  value={expectedDate}
                  onChange={setExpectedDate}
                />
                {selectedStatus.toLowerCase() === 'fechada' && (
                  <>
                    <DateRangeChip label='Entrega' value={endDate} onChange={setEndDate} />
                    <div className='d-flex flex-column'>
                      <label className='d-block fs-7 mb-1'>Pontualidade da entrega</label>
                      <select
                        className='form-select form-select-sm form-control form-control-solid place-holder w-100 w-md-200px tfb-native-select-md'
                        onChange={(e) =>
                          setDeliveryPunctuality(e.target.value as '' | 'on_time' | 'late')
                        }
                        value={deliveryPunctuality}
                      >
                        <option value=''>Todas</option>
                        <option value='on_time'>Entregues no prazo</option>
                        <option value='late'>Entregues com atraso</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Seção: Filtros adicionais */}
            <div className='d-flex flex-column gap-3 w-100 tfb-section-divider'>
              <span className='fs-7 text-uppercase fw-bold text-muted'>Filtros adicionais</span>
              <OverlayTrigger
                placement='bottom'
                delay={{show: 300, hide: 100}}
                overlay={
                  <Tooltip id='tooltip-with-subtasks' style={{maxWidth: '300px'}}>
                    <div className='text-start'>
                      <strong>Mostrar somente tarefas com subtarefas</strong>
                      <br />
                      Exibe apenas tarefas que possuem subtarefas associadas.
                    </div>
                  </Tooltip>
                }
              >
                <label className='form-check form-switch form-check-custom form-check-solid d-flex align-items-center'>
                  <input
                    className='form-check-input h-20px w-30px me-2'
                    type='checkbox'
                    role='button'
                    onChange={handleChangeOnlyWithSubtasks}
                    checked={onlyWithSubtasks}
                  />
                  <i
                    className={`bi bi-list-nested me-2 ${
                      onlyWithSubtasks ? 'text-info' : 'text-muted'
                    }`}
                  ></i>
                  <span
                    className={`${
                      onlyWithSubtasks ? 'fw-bold text-gray-900' : 'text-gray-600'
                    } fs-7`}
                  >
                    Mostrar somente tarefas com subtarefas
                  </span>
                </label>
              </OverlayTrigger>
            </div>

            <div className='d-flex gap-4 justify-content-end mt-2'>
              <button
                type='button'
                className='btn btn-secondary py-2'
                onClick={handleClearFilterClick}
              >
                Limpar
              </button>
              <button type='button' className='btn btn-primary py-2' onClick={handleFilterClick}>
                Filtrar
              </button>
            </div>
          </Card.Body>
        </Accordion.Collapse>
      </Card>
      </Accordion>
    </div>
  )
}

export {TaskFilterBar}
